/**
 * Compliance API service — strictly isolated from existing lead intelligence services.
 * All endpoints proxy through /compliance-api in vite.config.ts to LightLink's compliance port.
 *
 * See dev plan: "NEVER mix with current lead APIs."
 */

import axios from "axios";
import type {
  AuditEventListResponse,
  AuditExportResponse,
  BatchDocumentUploadRequest,
  BatchDocumentUploadResponse,
  CaseActionItem,
  CaseActionListResponse,
  CaseActionStatus,
  CaseEvidencePack,
  CaseStatus,
  CaseV2RollupResponse,
  ControlTowerResponse,
  CreateWebhookSubscriptionRequest,
  CreateWebhookSubscriptionResponse,
  CompositeRiskRequest,
  CompositeRiskResponse,
  CopilotQueryRequest,
  CopilotQueryResponse,
  CreateLicenseCredentialRequest,
  CreateLicenseCredentialResponse,
  CreateSupportTicketRequest,
  CreateUserRequest,
  CreateWorkflowRequest,
  DocumentHubAnalyzeRequest,
  DocumentHubListResponse,
  DocumentStatusResponse,
  DocumentUploadRequest,
  DocumentUploadResponse,
  EcmrValidationRequest,
  FinancialExposureRequest,
  FinancialExposureResponse,
  InfraHealthResponse,
  InvoiceValidationRequest,
  LicenseCredentialItem,
  LicenseCredentialListResponse,
  LicenseCredentialPortfolioResponse,
  LicenseValidationResponse,
  LoginActivityListResponse,
  NotificationItem,
  NotificationListResponse,
  OperatorAnalyticsResponse,
  RegistryCheckResponse,
  RiskListResponse,
  SanctionsScreenRequest,
  SanctionsScreenResponse,
  SemanticSearchRequest,
  SemanticSearchResponse,
  ShipmentCaseDetail,
  ShipmentCaseItem,
  ShipmentCaseListResponse,
  ShipmentValidationRequest,
  SupportTicketItem,
  SupportTicketListResponse,
  TariffCalculationRequest,
  TariffCalculationResponse,
  UpdateWorkflowStatusRequest,
  UserItem,
  UserListResponse,
  VatValidationRequest,
  WebhookDeliveryItem,
  WebhookDeliveryListResponse,
  WebhookSubscriptionItem,
  WebhookSubscriptionListResponse,
  WebhookTestRequest,
  ValidationResponse,
  WorkflowItem,
  WorkflowConsistencyResponse,
  WorkflowListResponse,
  WorkflowReadinessResponse,
  WorkflowSummaryResponse,
} from "../types/compliance";

const api = axios.create({
  // Backend routes are mounted under /api/compliance/*.
  // Vite/nginx strip only /compliance-api, so we keep /api/compliance here.
  baseURL: "/compliance-api/api/compliance",
  timeout: 30_000,
});

const LONG_RUNNING_TIMEOUT_MS = 180_000;

// Shared tenant context — injected via auth
let _tenantId = "default";
let _complianceRole = "COMPLIANCE_ADMIN";
let _adminLicenseKey: string | null = null;

export function setComplianceContext(tenantId: string, role: string) {
  _tenantId = tenantId;
  _complianceRole = role;
}

export function getComplianceContext() {
  return { tenantId: _tenantId, complianceRole: _complianceRole };
}

export function setLinkLightAdminContext(tenantId: string, licenseKey: string) {
  _tenantId = tenantId;
  _complianceRole = "ROOT_ADMIN";
  _adminLicenseKey = licenseKey;
}

export function clearLinkLightAdminContext() {
  _adminLicenseKey = null;
}

// Attach compliance-specific auth headers
api.interceptors.request.use((config) => {
  const token = _adminLicenseKey ?? localStorage.getItem("compliance_token");
  const mfa = localStorage.getItem("compliance_mfa");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  config.headers["X-Tenant-ID"] = _tenantId;
  config.headers["X-Compliance-Role"] = _complianceRole;
  if (mfa === "true") config.headers["X-Compliance-MFA"] = "verified";
  return config;
});

// On 401/403, redirect to compliance login
api.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err.response?.status === 401 || err.response?.status === 403) {
      if (_adminLicenseKey) {
        return Promise.reject(err);
      }
      [
        "compliance_token",
        "compliance_tenant_id",
        "compliance_role",
        "compliance_mfa",
      ].forEach((k) => localStorage.removeItem(k));
      window.location.href = "/Compliance/logistics/login";
    }
    return Promise.reject(err);
  },
);

// ── Health ──────────────────────────────────────────────────────────────

export async function getInfraHealth(): Promise<InfraHealthResponse> {
  const { data } = await api.get<InfraHealthResponse>("/health/infra");
  return data;
}

export async function verifyLicense(): Promise<LicenseValidationResponse> {
  const { data } = await api.get<LicenseValidationResponse>(
    "/auth/license/verify",
  );
  return data;
}

// ── Document Intake ─────────────────────────────────────────────────────

export async function uploadDocument(
  payload: DocumentUploadRequest,
): Promise<DocumentUploadResponse> {
  const { data } = await api.post<DocumentUploadResponse>(
    "/documents/upload",
    payload,
  );
  return data;
}

export async function uploadDocumentBatch(
  payload: BatchDocumentUploadRequest,
): Promise<BatchDocumentUploadResponse> {
  const { data } = await api.post<BatchDocumentUploadResponse>(
    "/documents/batch-upload",
    payload,
    { timeout: LONG_RUNNING_TIMEOUT_MS },
  );
  return data;
}

export async function listDocumentHub(
  batchId?: string,
): Promise<DocumentHubListResponse> {
  const { data } = await api.get<DocumentHubListResponse>("/documents/hub", {
    params: { tenant_id: _tenantId, batch_id: batchId || undefined },
  });
  return data;
}

export async function analyzeDocumentHubBatch(
  payload: DocumentHubAnalyzeRequest,
): Promise<BatchDocumentUploadResponse> {
  const { data } = await api.post<BatchDocumentUploadResponse>(
    "/documents/hub/analyze",
    { ...payload, tenant_id: _tenantId },
    { timeout: LONG_RUNNING_TIMEOUT_MS },
  );
  return data;
}

export async function getDocumentStatus(
  documentId: string,
): Promise<DocumentStatusResponse> {
  const { data } = await api.get<DocumentStatusResponse>(
    `/documents/${documentId}`,
    { params: { tenant_id: _tenantId } },
  );
  return data;
}

export async function listShipmentCases(): Promise<ShipmentCaseListResponse> {
  const { data } = await api.get<ShipmentCaseListResponse>("/cases", {
    params: { tenant_id: _tenantId },
  });
  return data;
}

export async function getShipmentCase(
  caseId: string,
): Promise<ShipmentCaseDetail> {
  const { data } = await api.get<ShipmentCaseDetail>(`/cases/${caseId}`, {
    params: { tenant_id: _tenantId },
  });
  return data;
}

export async function getCaseV2Rollup(
  caseId: string,
  shipmentType = "eu_import",
): Promise<CaseV2RollupResponse> {
  const { data } = await api.get<CaseV2RollupResponse>(
    `/cases/${caseId}/v2-rollup`,
    { params: { tenant_id: _tenantId, shipment_type: shipmentType } },
  );
  return data;
}

export async function updateShipmentCaseStatus(
  caseId: string,
  status: CaseStatus,
  note?: string,
): Promise<ShipmentCaseItem> {
  const { data } = await api.patch<ShipmentCaseItem>(
    `/cases/${caseId}/status`,
    {
      tenant_id: _tenantId,
      status,
      note,
    },
  );
  return data;
}

export async function getCaseEvidencePack(
  caseId: string,
): Promise<CaseEvidencePack> {
  const { data } = await api.get<CaseEvidencePack>(
    `/cases/${caseId}/evidence-pack`,
    { params: { tenant_id: _tenantId } },
  );
  return data;
}

export async function listCaseActions(
  caseId: string,
): Promise<CaseActionListResponse> {
  const { data } = await api.get<CaseActionListResponse>(
    `/cases/${caseId}/actions`,
    { params: { tenant_id: _tenantId } },
  );
  return data;
}

export async function updateCaseActionStatus(
  caseId: string,
  actionId: string,
  status: CaseActionStatus,
  note?: string,
): Promise<CaseActionItem> {
  const { data } = await api.patch<CaseActionItem>(
    `/cases/${caseId}/actions/${actionId}`,
    {
      tenant_id: _tenantId,
      status,
      note,
    },
  );
  return data;
}

export async function getControlTower(): Promise<ControlTowerResponse> {
  const { data } = await api.get<ControlTowerResponse>("/control-tower", {
    params: { tenant_id: _tenantId },
  });
  return data;
}

// ── Risk Engine ─────────────────────────────────────────────────────────

export async function getOperatorAnalytics(): Promise<OperatorAnalyticsResponse> {
  const { data } = await api.get<OperatorAnalyticsResponse>(
    "/analytics/operator",
    { params: { tenant_id: _tenantId } },
  );
  return data;
}

export async function listRisks(
  workflowId?: string,
  batchId?: string,
): Promise<RiskListResponse> {
  const { data } = await api.get<RiskListResponse>("/risks", {
    params: {
      tenant_id: _tenantId,
      workflow_id: workflowId,
      batch_id: batchId,
    },
  });
  return data;
}

export async function computeCompositeRisk(
  workflowId: string,
): Promise<CompositeRiskResponse> {
  const payload: CompositeRiskRequest = {
    tenant_id: _tenantId,
    workflow_id: workflowId,
  };
  const { data } = await api.post<CompositeRiskResponse>(
    "/risk/composite",
    payload,
  );
  return data;
}

// ── Validation ──────────────────────────────────────────────────────────

export async function getWorkflowConsistency(
  workflowId: string,
): Promise<WorkflowConsistencyResponse> {
  const { data } = await api.get<WorkflowConsistencyResponse>(
    `/workflows/${workflowId}/consistency`,
    { params: { tenant_id: _tenantId } },
  );
  return data;
}

export async function getWorkflowReadiness(
  workflowId: string,
  shipmentType = "eu_import",
): Promise<WorkflowReadinessResponse> {
  const { data } = await api.get<WorkflowReadinessResponse>(
    `/workflows/${workflowId}/readiness`,
    { params: { tenant_id: _tenantId, shipment_type: shipmentType } },
  );
  return data;
}

export async function checkRegistryVat(
  countryCode: string,
  vatNumber: string,
): Promise<RegistryCheckResponse> {
  const { data } = await api.get<RegistryCheckResponse>(
    `/registry/vat/${countryCode}/${vatNumber}`,
    { params: { tenant_id: _tenantId } },
  );
  return data;
}

export async function checkRegistryEori(
  eoriNumber: string,
): Promise<RegistryCheckResponse> {
  const { data } = await api.get<RegistryCheckResponse>(
    `/registry/eori/${eoriNumber}`,
    { params: { tenant_id: _tenantId } },
  );
  return data;
}

export async function checkTaricHsCode(
  hsCode: string,
  originCountry?: string,
  destinationCountry?: string,
): Promise<RegistryCheckResponse> {
  const { data } = await api.get<RegistryCheckResponse>(
    `/tariff/hs/${hsCode}`,
    {
      params: {
        tenant_id: _tenantId,
        origin_country: originCountry,
        destination_country: destinationCountry,
      },
    },
  );
  return data;
}

export async function screenSanctions(
  payload: Omit<SanctionsScreenRequest, "tenant_id">,
): Promise<SanctionsScreenResponse> {
  const { data } = await api.post<SanctionsScreenResponse>(
    "/sanctions/screen",
    {
      ...payload,
      tenant_id: _tenantId,
    },
  );
  return data;
}

export async function calculateFinancialExposure(
  payload: Omit<FinancialExposureRequest, "tenant_id">,
): Promise<FinancialExposureResponse> {
  const { data } = await api.post<FinancialExposureResponse>(
    "/financial-exposure",
    { ...payload, tenant_id: _tenantId },
  );
  return data;
}

export async function calculateTariff(
  payload: Omit<TariffCalculationRequest, "tenant_id">,
): Promise<TariffCalculationResponse> {
  const { data } = await api.post<TariffCalculationResponse>(
    "/tariff/calculate",
    { ...payload, tenant_id: _tenantId },
  );
  return data;
}

export async function validateVat(
  payload: VatValidationRequest,
): Promise<ValidationResponse> {
  const { data } = await api.post<ValidationResponse>("/vat/validate", {
    ...payload,
    tenant_id: _tenantId,
  });
  return data;
}

export async function validateInvoice(
  payload: InvoiceValidationRequest,
): Promise<ValidationResponse> {
  const { data } = await api.post<ValidationResponse>("/invoices/validate", {
    ...payload,
    tenant_id: _tenantId,
  });
  return data;
}

export async function validateShipment(
  payload: ShipmentValidationRequest,
): Promise<ValidationResponse> {
  const { data } = await api.post<ValidationResponse>("/shipments/validate", {
    ...payload,
    tenant_id: _tenantId,
  });
  return data;
}

export async function validateEcmr(
  payload: EcmrValidationRequest,
): Promise<ValidationResponse> {
  const { data } = await api.post<ValidationResponse>("/ecmr/validate", {
    ...payload,
    tenant_id: _tenantId,
  });
  return data;
}

// ── Audit ───────────────────────────────────────────────────────────────

export async function searchAuditEvents(params: {
  check_type?: string;
  from_date?: string;
  to_date?: string;
  workflow_id?: string;
  batch_id?: string;
  page?: number;
  page_size?: number;
}): Promise<AuditEventListResponse> {
  const { data } = await api.get<AuditEventListResponse>("/audit/search", {
    params: { tenant_id: _tenantId, ...params },
  });
  return data;
}

export async function listAuditEvents(
  workflowId?: string,
  batchId?: string,
): Promise<AuditEventListResponse> {
  const { data } = await api.get<AuditEventListResponse>("/audit/events", {
    params: {
      tenant_id: _tenantId,
      workflow_id: workflowId,
      batch_id: batchId,
    },
  });
  return data;
}

export async function exportAuditReport(params: {
  export_format?: "json" | "csv";
  check_type?: string;
  from_date?: string;
  to_date?: string;
  workflow_id?: string;
  batch_id?: string;
}): Promise<AuditExportResponse> {
  const { data } = await api.get<AuditExportResponse>("/audit/export", {
    params: { tenant_id: _tenantId, ...params },
  });
  return data;
}

// ── Workflow ────────────────────────────────────────────────────────────

export async function getWorkflowSummary(
  workflowId: string,
): Promise<WorkflowSummaryResponse> {
  const { data } = await api.get<WorkflowSummaryResponse>(
    `/workflows/${workflowId}`,
    { params: { tenant_id: _tenantId } },
  );
  return data;
}

// ── Copilot ─────────────────────────────────────────────────────────────

export async function copilotQuery(
  payload: CopilotQueryRequest,
): Promise<CopilotQueryResponse> {
  const { data } = await api.post<CopilotQueryResponse>("/copilot/query", {
    ...payload,
    tenant_id: _tenantId,
  });
  return data;
}

// ── Workflow Management ─────────────────────────────────────────────────

export async function listWorkflows(
  tenantId?: string,
): Promise<WorkflowListResponse> {
  const { data } = await api.get<WorkflowListResponse>("/workflows", {
    params: { tenant_id: tenantId ?? _tenantId },
  });
  return data;
}

export async function createWorkflow(
  payload: CreateWorkflowRequest,
): Promise<WorkflowItem> {
  const { data } = await api.post<WorkflowItem>("/workflows", {
    ...payload,
    tenant_id: _tenantId,
  });
  return data;
}

export async function updateWorkflowStatus(
  workflowId: string,
  payload: UpdateWorkflowStatusRequest,
): Promise<WorkflowItem> {
  const { data } = await api.patch<WorkflowItem>(`/workflows/${workflowId}`, {
    ...payload,
    tenant_id: _tenantId,
  });
  return data;
}

// ── User Management ─────────────────────────────────────────────────────

export async function listUsers(tenantId?: string): Promise<UserListResponse> {
  const { data } = await api.get<UserListResponse>("/users", {
    params: { tenant_id: tenantId ?? _tenantId },
  });
  return data;
}

export async function createUser(
  payload: CreateUserRequest,
): Promise<UserItem> {
  const { data } = await api.post<UserItem>("/users", {
    ...payload,
    tenant_id: _tenantId,
  });
  return data;
}

export async function listLicenseCredentials(
  tenantId?: string,
): Promise<LicenseCredentialListResponse> {
  const { data } = await api.get<LicenseCredentialListResponse>(
    "/admin/licenses",
    { params: { tenant_id: tenantId ?? _tenantId } },
  );
  return data;
}

export async function listAllTenantLicenses(): Promise<LicenseCredentialPortfolioResponse> {
  const { data } = await api.get<LicenseCredentialPortfolioResponse>(
    "/admin/licenses/all",
  );
  return data;
}

export async function createLicenseCredential(
  payload: CreateLicenseCredentialRequest,
): Promise<CreateLicenseCredentialResponse> {
  const { data } = await api.post<CreateLicenseCredentialResponse>(
    "/admin/licenses",
    payload,
  );
  return data;
}

export async function updateLicenseCredentialStatus(
  licenseId: string,
  status: "active" | "revoked",
  tenantId?: string,
): Promise<LicenseCredentialItem> {
  const { data } = await api.patch<LicenseCredentialItem>(
    `/admin/licenses/${licenseId}`,
    { tenant_id: tenantId ?? _tenantId, status },
  );
  return data;
}

export async function reissueLicenseCredential(
  licenseId: string,
  tenantId?: string,
): Promise<CreateLicenseCredentialResponse> {
  const { data } = await api.post<CreateLicenseCredentialResponse>(
    `/admin/licenses/${licenseId}/reissue`,
    { tenant_id: tenantId ?? _tenantId },
  );
  return data;
}

export async function listWebhookSubscriptions(
  tenantId?: string,
): Promise<WebhookSubscriptionListResponse> {
  const { data } = await api.get<WebhookSubscriptionListResponse>(
    "/integrations/webhooks",
    { params: { tenant_id: tenantId ?? _tenantId } },
  );
  return data;
}

export async function createWebhookSubscription(
  payload: CreateWebhookSubscriptionRequest,
): Promise<CreateWebhookSubscriptionResponse> {
  const { data } = await api.post<CreateWebhookSubscriptionResponse>(
    "/integrations/webhooks",
    { ...payload, tenant_id: payload.tenant_id ?? _tenantId },
  );
  return data;
}

export async function updateWebhookSubscriptionStatus(
  subscriptionId: string,
  status: "active" | "paused" | "revoked",
  tenantId?: string,
): Promise<WebhookSubscriptionItem> {
  const { data } = await api.patch<WebhookSubscriptionItem>(
    `/integrations/webhooks/${subscriptionId}`,
    { tenant_id: tenantId ?? _tenantId, status },
  );
  return data;
}

export async function listWebhookDeliveries(params?: {
  tenant_id?: string;
  subscription_id?: string;
  status?: string;
}): Promise<WebhookDeliveryListResponse> {
  const { data } = await api.get<WebhookDeliveryListResponse>(
    "/integrations/webhooks/deliveries",
    {
      params: {
        tenant_id: params?.tenant_id ?? _tenantId,
        subscription_id: params?.subscription_id,
        status: params?.status,
      },
    },
  );
  return data;
}

export async function testWebhookSubscription(
  subscriptionId: string,
  payload?: Partial<WebhookTestRequest>,
): Promise<WebhookDeliveryListResponse> {
  const { data } = await api.post<WebhookDeliveryListResponse>(
    `/integrations/webhooks/${subscriptionId}/test`,
    {
      tenant_id: payload?.tenant_id ?? _tenantId,
      event_type: payload?.event_type ?? "integration.test",
      payload: payload?.payload ?? { source: "frontend" },
    },
  );
  return data;
}

export async function replayWebhookDelivery(
  deliveryId: string,
  tenantId?: string,
): Promise<WebhookDeliveryItem> {
  const { data } = await api.post<WebhookDeliveryItem>(
    `/integrations/webhooks/deliveries/${deliveryId}/replay`,
    { tenant_id: tenantId ?? _tenantId },
  );
  return data;
}

export async function listLoginActivity(
  tenantId?: string,
): Promise<LoginActivityListResponse> {
  const { data } = await api.get<LoginActivityListResponse>(
    "/admin/login-activity",
    { params: { tenant_id: tenantId ?? _tenantId } },
  );
  return data;
}

export async function listSupportTickets(
  tenantId?: string,
): Promise<SupportTicketListResponse> {
  const { data } = await api.get<SupportTicketListResponse>(
    "/admin/support-tickets",
    { params: { tenant_id: tenantId ?? _tenantId } },
  );
  return data;
}

export async function createSupportTicket(
  payload: CreateSupportTicketRequest,
): Promise<SupportTicketItem> {
  const { data } = await api.post<SupportTicketItem>(
    "/admin/support-tickets",
    payload,
  );
  return data;
}

// ── Notifications ───────────────────────────────────────────────────────

export async function listNotifications(params?: {
  workflow_id?: string;
  unread_only?: boolean;
}): Promise<NotificationListResponse> {
  const { data } = await api.get<NotificationListResponse>("/notifications", {
    params: { tenant_id: _tenantId, ...params },
  });
  return data;
}

export async function markNotificationRead(
  notificationId: string,
): Promise<NotificationItem> {
  const { data } = await api.patch<NotificationItem>(
    `/notifications/${notificationId}`,
    null,
    { params: { tenant_id: _tenantId } },
  );
  return data;
}

export async function copilotSearch(
  payload: SemanticSearchRequest,
): Promise<SemanticSearchResponse> {
  const { data } = await api.post<SemanticSearchResponse>("/copilot/search", {
    ...payload,
    tenant_id: _tenantId,
  });
  return data;
}

// ── Dataset Browser ─────────────────────────────────────────────────────

export interface SampleDocumentMeta {
  filename: string;
  doc_type: string;
  label: string;
  format: string;
  size_bytes: number;
  available: boolean;
}

export interface SampleDocumentContent {
  filename: string;
  mime_type: string;
  content_base64: string;
  size_bytes: number;
}

export async function listSampleDocuments(): Promise<SampleDocumentMeta[]> {
  const { data } = await api.get<{ samples: SampleDocumentMeta[] }>(
    "/datasets/samples",
  );
  return data.samples;
}

export async function getSampleDocumentContent(
  filename: string,
): Promise<SampleDocumentContent> {
  const { data } = await api.get<SampleDocumentContent>(
    `/datasets/samples/${encodeURIComponent(filename)}`,
  );
  return data;
}
